/**
 * Filter Settings API Service
 * Manages filter conditions and logic operators via backend API
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { FilterCondition } from '../Components/Common/AppFilter/FilterRow';

export interface FilterSettings {
  filterId: string;
  conditions: FilterCondition[];
  logicOperator: 'AND' | 'OR';
  expiredAt?: string;
}

export interface SaveFilterSettingsRequest {
  filterId: string;
  conditions: FilterCondition[];
  logicOperator: 'AND' | 'OR';
  expiredAt?: string;
}

export const filterSettingsApi = createApi({
  reducerPath: 'filterSettingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['FilterSettings'],
  endpoints: (builder) => ({
    // Get filter settings
    getFilterSettings: builder.query<FilterSettings | null, string>({
      query: (filterId) => `/settings/filter/${filterId}`,
      transformResponse: (response: any): FilterSettings | null => {
        if (response && typeof response === 'object' && 'data' in response) {
          return response.data;
        }
        if (response && typeof response === 'object' && !('status' in response)) {
          return response;
        }
        return null;
      },
      providesTags: (result, error, filterId) => [
        { type: 'FilterSettings', id: filterId },
      ],
    }),

    // Save filter settings
    saveFilterSettings: builder.mutation<any, SaveFilterSettingsRequest>({
      query: (data) => ({
        url: '/settings/filter/save',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        { type: 'FilterSettings', id: data.filterId },
      ],
    }),

    // Reset filter settings
    resetFilterSettings: builder.mutation<any, string>({
      query: (filterId) => ({
        url: `/settings/filter/${filterId}/reset`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, filterId) => [
        { type: 'FilterSettings', id: filterId },
      ],
    }),
  }),
});

export const {
  useGetFilterSettingsQuery,
  useLazyGetFilterSettingsQuery,
  useSaveFilterSettingsMutation,
  useResetFilterSettingsMutation,
} = filterSettingsApi;
